import { db } from '@/lib/firebase'
import type { User, UserInput } from '@/types'
import { UserType } from '@/types/user'
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as limitFirestore,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { sendVerificationEmail } from './emailVerificationService'
import { completePendingOwnershipTransfer } from './ownershipService'

const USERS_COLLECTION = 'users'

export { USERS_COLLECTION }

export const createUser = async (id: string, input: UserInput) => {
  try {
    const docRef = doc(collection(db, USERS_COLLECTION), id)

    const { confirmPassword, password, ...userData } = input as UserInput & {
      confirmPassword?: string
      password?: string
    }

    const newUser: User = {
      id,
      ...userData,
      userType: userData.userType || UserType.USER,
      emailVerified: false,
      createdAt: Timestamp.now(),
    }

    await setDoc(docRef, newUser)

    // Note: Verification email is sent automatically by onAuthUserCreate trigger
    // No need to call it manually here

    if (userData.email) {
      try {
        await completePendingOwnershipTransfer(userData.email, id)
      } catch (error) {}
    }

    return newUser
  } catch (error: any) {
    throw new Error(`Failed to create user: ${error.message}`)
  }
}

export const updateUser = async (id: string, input: Partial<UserInput>) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, id)
    await updateDoc(docRef, {
      ...input,
      updatedAt: Timestamp.now(),
    })

    // Return the updated user data
    const updatedDoc = await getDoc(docRef)
    if (updatedDoc.exists()) {
      return { id: updatedDoc.id, ...updatedDoc.data() } as User
    }

    throw new Error('User not found after update')
  } catch (error: any) {
    throw new Error(`Failed to update user: ${error.message}`)
  }
}

export const getUserById = async (id: string): Promise<User | null> => {
  try {
    const docRef = doc(db, USERS_COLLECTION, id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as User
    }

    return null
  } catch (error: any) {
    throw new Error(`Failed to get user: ${error.message}`)
  }
}

export const getUsersByIDs = async (userIds: string[]): Promise<User[]> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const q = query(usersRef, where('id', 'in', userIds))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User)
  } catch (error: any) {
    throw new Error(`Failed to get users by IDs: ${error.message}`)
  }
}

export const getUsersByTypes = async (userTypes: UserType[]): Promise<User[]> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const q = query(usersRef, where('userType', 'in', userTypes))
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User)
  } catch (error: any) {
    throw new Error(`Failed to get users: ${error.message}`)
  }
}

export const getUserByEmail = async (email: string): Promise<User> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const q = query(usersRef, where('email', '==', email), limitFirestore(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      throw new Error('User not found')
    }

    const userDoc = querySnapshot.docs[0]
    return { id: userDoc.id, ...userDoc.data() } as User
  } catch (error: any) {
    throw new Error(`Failed to get user by email: ${error.message}`)
  }
}

export const getUsersByEmail = async (email: string): Promise<User[]> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const q = query(usersRef, where('email', '==', email))
    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User)
  } catch (error: any) {
    throw new Error(`Failed to get users by email: ${error.message}`)
  }
}

export const deleteUser = async (id: string) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, id)
    await deleteDoc(docRef)
  } catch (error: any) {
    throw new Error(`Failed to delete user: ${error.message}`)
  }
}

export const getUsers = async (lastVisible?: any, limitCount = 10): Promise<{ users: User[]; lastVisible: any }> => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    let q = query(usersRef, orderBy('createdAt', 'desc'), limitFirestore(limitCount))

    if (lastVisible) {
      q = query(usersRef, orderBy('createdAt', 'desc'), limitFirestore(limitCount))
    }

    const querySnapshot = await getDocs(q)
    const users = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User)
    const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null

    return { users, lastVisible: newLastVisible }
  } catch (error: any) {
    throw new Error(`Failed to get users: ${error.message}`)
  }
}

export const addUser = async (input: UserInput) => {
  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const docRef = await addDoc(usersRef, {
      ...input,
      userType: input.userType || UserType.USER,
      emailVerified: false,
      createdAt: Timestamp.now(),
    })

    return docRef.id
  } catch (error: any) {
    throw new Error(`Failed to add user: ${error.message}`)
  }
}

export const followUser = async (userId: string, userToFollowId: string) => {
  try {
    const userFollowsRef = collection(db, 'userFollows')
    await addDoc(userFollowsRef, {
      followerId: userId,
      followingId: userToFollowId,
      createdAt: Timestamp.now(),
    })
  } catch (error: any) {
    throw new Error(`Failed to follow user: ${error.message}`)
  }
}

export const unfollowUser = async (userId: string, userToUnfollowId: string) => {
  try {
    const userFollowsRef = collection(db, 'userFollows')
    const q = query(userFollowsRef, where('followerId', '==', userId), where('followingId', '==', userToUnfollowId))
    const querySnapshot = await getDocs(q)

    if (!querySnapshot.empty) {
      const docToDelete = querySnapshot.docs[0]
      await deleteDoc(doc(db, 'userFollows', docToDelete.id))
    }
  } catch (error: any) {
    throw new Error(`Failed to unfollow user: ${error.message}`)
  }
}

export const getFollowedUsers = async (userId: string): Promise<User[]> => {
  // Get users that this user follows
  const followsQuery = query(collection(db, 'follows'), where('userId', '==', userId))

  const followsSnapshot = await getDocs(followsQuery)
  const followedUserIds = followsSnapshot.docs.map((doc) => doc.data().companyUserId)

  if (followedUserIds.length === 0) return []

  return getUsersByIDs(followedUserIds)
}

export const getAllUsers = async (limitCount = 50): Promise<User[]> => {
  const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limitFirestore(limitCount))

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as User)
}
